"use client";

import React from "react";
import { MotionWrapper } from "../MotionWrapper";
import Image from "next/image";
import Link from "next/link";
import Script from "next/script";
import { ExternalLink } from "lucide-react";

const clientsData = [
  {
    name: "CraftiGames",
    logo: "/craftigames-logo.png",
    link: "https://craftigames.com",
    description: "Game Company",
  },
  {
    name: "PikaNetwork",
    logo: "/pikanetwork-logo.png",
    link: "https://pika-network.net/",
    description: "Minecraft Server",
  },
  {
    name: "JartexNetwork",
    logo: "/jartexnetwork-logo.png",
    link: "https://jartexnetwork.com",
    description: "Minecraft Server",
  },
  {
    name: "1UP Nova",
    logo: "/1upnova-logo.png",
    link: "https://1upnova.com",
    description: "Gaming Platform",
  },
  {
    name: "JungleRealms",
    logo: "/junglerealms-logo.png",
    link: "https://junglerealms.com",
    description: "Minecraft Server",
  },
  {
    name: "JukeboxMC",
    logo: "/jukeboxmclogo.png",
    link: "https://jukeboxmc.net",
    description: "Minecraft Server",
  },
  {
    name: "Mineplex",
    logo: "/mineplex-logo.svg",
    link: "https://www.mineplex.com",
    description: "Minecraft Server",
  },
  {
    name: "BeyondMC",
    logo: "/beyondmc-logo.png",
    link: "https://www.beyondmc.net",
    description: "Minecraft Server",
  },
];

export default function ClientsSection() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Thiemo Boven",
    url: "https://lunaty.net",
    jobTitle: "Software Engineer & Lead Developer",
    worksFor: clientsData.map((client) => ({
      "@type": "Organization",
      name: client.name,
      url: client.link,
      logo: `https://lunaty.net${client.logo}`,
    })),
  };

  return (
    <>
      <Script id="clients-structured-data" type="application/ld+json">
        {JSON.stringify(structuredData)}
      </Script>
      <section
        id="clients"
        className="relative z-10 py-20 px-4 max-w-screen-2xl mx-auto"
        aria-label="Client Portfolio"
      >
        <div className="max-w-6xl mx-auto">
          <MotionWrapper
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-[#FFD700]">My</span> Clients
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Trusted by leading gaming platforms and communities worldwide
            </p>
          </MotionWrapper>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {clientsData.slice(0, 4).map((client, index) => (
              <MotionWrapper
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <ClientLogo {...client} />
              </MotionWrapper>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-8">
            {clientsData.slice(4).map((client, index) => (
              <MotionWrapper
                key={index + 4}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: (index + 4) * 0.1 }}
              >
                <ClientLogo {...client} />
              </MotionWrapper>
            ))}
          </div>
        </div>

        <div className="absolute top-1/4 left-0 w-32 h-32 bg-gradient-to-r from-[#FFD700]/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-40 h-40 bg-gradient-to-l from-[#FFD700]/10 to-transparent rounded-full blur-3xl" />
      </section>
    </>
  );
}

type ClientLogoProps = {
  name: string;
  logo: string;
  link: string;
  description: string;
};

function ClientLogo({ name, logo, link, description }: ClientLogoProps) {
  return (
    <MotionWrapper
      whileHover={{
        scale: 1.05,
        y: -5,
        transition: { duration: 0.2 },
      }}
      className="group relative"
    >
      <div className="bg-gray-900/30 backdrop-blur-sm px-6 py-8 rounded-xl border border-gray-900 flex flex-col items-center justify-center h-[180px] hover:border-[#FFD700]/30 transition-all duration-300 hover:shadow-lg hover:shadow-[#FFD700]/10">
        <div className="relative w-full h-full flex items-center justify-center flex-col">
          <Image
            src={logo || "/placeholder.svg"}
            alt={`${name} logo - Client of Thiemo Boven at Lunaty`}
            width={150}
            height={75}
            className="max-w-[80%] max-h-[60%] w-auto h-auto object-contain group-hover:scale-110 transition-transform duration-300"
            priority={true}
          />

          <div className="mt-4 text-center">
            <h4 className="text-white font-medium mb-1 group-hover:text-[#FFD700] transition-colors duration-300">
              {name}
            </h4>
            <p className="text-gray-400 text-xs mb-2">{description}</p>
            <Link
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-[#FFD700] hover:text-[#FFA500] transition-colors duration-300 text-sm font-medium"
              aria-label={`Visit ${name}'s website`}
            >
              Visit Site
              <ExternalLink
                size={12}
                className="ml-1 group-hover:translate-x-1 transition-transform duration-300"
              />
            </Link>
          </div>
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFD700]/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>
    </MotionWrapper>
  );
}
